// Login.js

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Mail,
  Shield,
  ArrowRight,
  User,
  CheckCircle,
  Clock,
  X,
  Image,
  Zap,
  Layers,
  Users,
  MousePointer,
  Monitor,
  Lock
} from "react-feather";

import "./Login.css";

import Alert from "../../components/Alert/Alert";
import { saveSession, getSession, clearSession } from "../../utils/authSession";
import {
  checkAccess,
  checkUser,
  sendAccessCode,
  portalSendAccessCode,
  portalCheckAccessCode,
  getUser,
  requestAccess,
  completeCompanySetup
} from "../../api/LoginApi";

function LoadingSpinner({ small = false }) {
  return (
    <span className={`login-spinner ${small ? " small" : ""}`} />
  );
}


export default function Login({ onLogin }) {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const [step, setStep] = useState("email");
  const [pendingLoginPayload, setPendingLoginPayload] = useState(null);
  const [companyCode, setCompanyCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");

  const [companyName, setCompanyName] = useState("");
  const companyValid = companyName.trim().length >= 2;
  const canContinueCompany = companyValid && !loading;

  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);
  const [accessRequestOpen, setAccessRequestOpen] = useState(false);
  const [accessRequestName, setAccessRequestName] = useState("");
  const [accessRequestLoading, setAccessRequestLoading] = useState(false);
  const [accessRequestState, setAccessRequestState] = useState("form");
  const [accessRequestMessage, setAccessRequestMessage] = useState("");
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const codeRef = useRef(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    if (!loginModalOpen || step !== "email") return undefined;

    const focusTimer = setTimeout(() => {
      emailInputRef.current?.focus();
    }, 60);

    return () => clearTimeout(focusTimer);
  }, [loginModalOpen, step]);

  function openLoginModal() {
    setLoginModalOpen(true);

    // Keep focus tied to the user's click so mobile browsers can open the keyboard.
    requestAnimationFrame(() => {
      emailInputRef.current?.focus();
    });
  }

  function closeLoginModal() {
    if (loading) return;
    setLoginModalOpen(false);
  }

  useEffect(() => {
    async function validateSession() {
      const session = getSession();

      if (!session?.email) return;

      try {
        const user = await getUser(session.userId, session.email);

        if (!user || user.status !== "active") {
          clearSession();
          return;
        }

        onLogin({
          ...session,
          ...user
        });

      } catch (err) {
        clearSession();
      }
    }

    validateSession();
  }, [onLogin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const approvedEmail = String(params.get("email") || "").toLowerCase().trim();
    const setupToken = String(params.get("setup") || "").trim();

    if (!approvedEmail || !setupToken) return;

    let cancelled = false;

    async function openApprovedRegistration() {
      try {
        setLoading(true);
        setAlert(null);

        const data = await checkUser(approvedEmail);
        const approvedUser = data?.user || data;

        if (cancelled || !approvedUser?.canCreateCompany || approvedUser?.companyId) return;

        setEmail(approvedEmail);
        setPendingLoginPayload({
          userId: null,
          email: approvedEmail,
          status: "active",
          role: "owner",
          companyId: null,
          companyName: null,
          canCreateCompany: true,
          companySetupToken: setupToken
        });
        setCompanyName("");
        setStep("company");
        setLoginModalOpen(true);

        const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.hash || ""}`;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (err) {
        if (!cancelled) {
          setEmail(approvedEmail);
          setAlert({
            type: "error",
            message: "This approval link is no longer valid. Please continue with your email."
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    openApprovedRegistration();

    return () => {
      cancelled = true;
    };
  }, []);

  const emailValid =
    email.includes("@") &&
    email.includes(".") &&
    email.trim().length > 5;

  const maskedEmail = email.replace(/(.{2}).+(@.+)/, "$1***$2");

  async function handleContinue() {
    if (!emailValid || loading) return;

    try {
      setLoading(true);
      setAlert(null);

      const user = await checkUser(
        email.trim().toLowerCase()
      );

      // Only company users have a company name
      if (user.companyName) {
        setCompanyName(user.companyName);

        await sendAccessCode(
          email.trim().toLowerCase()
        );
      } else {
        await sendAccessCode(
          email.trim().toLowerCase()
        );
      }

      setStep("code");

      setTimeout(() => {
        codeRef.current?.focus();
      }, 250);

    } catch (err) {

      setAlert(null);

      const isInactiveUser = err.code === "USER_INACTIVE";
      const isPendingRequest = err.code === "ACCESS_REQUEST_PENDING";
      const isUnknownUser =
        err.code === "USER_NOT_FOUND" ||
        (err.status === 403 && !isInactiveUser && !isPendingRequest);

      if (isPendingRequest) {
        setLoginModalOpen(false);
        setAccessRequestName("");
        setAccessRequestState("pending");
        setAccessRequestMessage("");
        setAccessRequestOpen(true);
      } else if (isUnknownUser) {
        setLoginModalOpen(false);
        setAccessRequestName("");
        setAccessRequestState("form");
        setAccessRequestMessage("");
        setAccessRequestOpen(true);
      } else {
        setLoginModalOpen(false);
        setAccessDeniedOpen(true);
      }

    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmCode() {
    if (code.length !== 6 || loading) return;

    try {
      setLoading(true);
      setAlert(null);

      let data;

      data = await checkAccess(
        email.trim().toLowerCase(),
        code
      );
      const user = await getUser(
        data.user.indexUserId || data.user.id || data.user.userId,
        data.user.email
      );

      if (!user || user.status !== "active") {
        throw new Error("This user is not active.");
      }

      const payload = {
        userId: user.indexUserId || user.id || null,
        email: user.email,
        status: user.status,
        role: user.role || null,
        companyId: user.companyId || null,
        companyName: user.companyName || null,
        canCreateCompany: user.canCreateCompany || false,
        companySetupToken: data.companySetupToken || null
      };

      if (
        payload.role === "owner" &&
        !payload.companyId
      ) {
        setPendingLoginPayload(payload);
        setCompanyName(user.companyName || "");
        setStep("company");
        return;
      }

      saveSession(payload);
      onLogin(payload);

    } catch (err) {

      setAlert({
        type: "error",
        message: err.message
      });

      setCode("");

      setTimeout(() => {
        codeRef.current?.focus();
      }, 100);

    } finally {

      setLoading(false);

    }
  }

  async function handleResendCode() {

    if (loading || resendCountdown > 0) return;

    try {

      setLoading(true);
      setAlert(null);

      await sendAccessCode(
        email.trim().toLowerCase()
      );

      setCode("");

      setResendMessage("A new access code has been sent.");

      let count = 10;

      setResendCountdown(count);

      const timer = setInterval(() => {

        count--;

        setResendCountdown(count);

        if (count <= 0) {

          clearInterval(timer);

          setResendMessage("");

        }

      }, 1000);

    } catch (err) {

      setAlert({
        type: "error",
        message: err.message
      });

    } finally {

      setLoading(false);

    }

  }

  async function handleCreateCompanySendCode() {
    if (!canContinueCompany || loading) return;

    try {
      setLoading(true);
      setAlert(null);

      if (pendingLoginPayload?.companySetupToken) {
        const data = await completeCompanySetup(
          companyName.trim(),
          email.trim().toLowerCase(),
          pendingLoginPayload.companySetupToken
        );

        const updatedPayload = {
          ...pendingLoginPayload,
          userId: data.user.id || pendingLoginPayload.userId || null,
          email: data.user.email || pendingLoginPayload.email,
          status: "active",
          role: "owner",
          companyId: data.company.id,
          companyName: data.company.name,
          companySetupToken: null
        };

        saveSession(updatedPayload);
        onLogin(updatedPayload);
        return;
      }

      // Compatibility fallback for any older registration session that does
      // not contain the new verified company setup token.
      await portalSendAccessCode(
        companyName.trim(),
        email.trim().toLowerCase()
      );

      setCompanyCode("");
      setStep("companyCode");

      setTimeout(() => {
        codeRef.current?.focus();
      }, 250);

    } catch (err) {
      setAlert({
        type: "error",
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  }


  async function handleConfirmCompanyCode() {
    if (companyCode.length !== 6 || loading || !pendingLoginPayload) return;

    try {
      setLoading(true);
      setAlert(null);

      const data = await portalCheckAccessCode(
        companyName.trim(),
        email.trim().toLowerCase(),
        companyCode
      );

      const updatedPayload = {
        ...pendingLoginPayload,
        userId: pendingLoginPayload.userId,
        email: pendingLoginPayload.email,
        status: "active",
        role: "owner",
        companyId: data.company.id,
        companyName: data.company.name
      };

      saveSession(updatedPayload);
      onLogin(updatedPayload);

    } catch (err) {
      setAlert({
        type: "error",
        message: err.message
      });

      setCompanyCode("");

      setTimeout(() => {
        codeRef.current?.focus();
      }, 100);

    } finally {
      setLoading(false);
    }
  }


  async function handleResendCompanyCode() {
    if (loading || resendCountdown > 0) return;

    try {
      setLoading(true);
      setAlert(null);

      await portalSendAccessCode(
        companyName.trim(),
        email.trim().toLowerCase()
      );

      setCompanyCode("");
      setResendMessage("A new access code has been sent.");

      let count = 10;
      setResendCountdown(count);

      const timer = setInterval(() => {
        count--;
        setResendCountdown(count);

        if (count <= 0) {
          clearInterval(timer);
          setResendMessage("");
        }
      }, 1000);

    } catch (err) {
      setAlert({
        type: "error",
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestAccess() {
    const cleanName = accessRequestName.trim();
    const cleanEmail = email.trim().toLowerCase();

    if (cleanName.length < 2 || accessRequestLoading) return;

    try {
      setAccessRequestLoading(true);
      setAccessRequestMessage("");

      const data = await requestAccess(cleanName, cleanEmail);

      if (data.alreadyAuthorized) {
        setAccessRequestState("authorized");
        return;
      }

      setAccessRequestState(data.alreadyPending ? "pending" : "success");
    } catch (err) {
      setAccessRequestMessage(
        err.message || "We couldn't submit your request. Please try again."
      );
    } finally {
      setAccessRequestLoading(false);
    }
  }

  function closeAccessRequest() {
    if (accessRequestLoading) return;
    setAccessRequestOpen(false);
    setAccessRequestMessage("");
  }

  return (

    <main className="login-page">

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
        />
      )}

      <div className="login-experience">

        <section className="login-launch-hero">
          <div className="login-launch-space" aria-hidden="true">
            <span className="launch-star-field launch-stars-a" />
            <span className="launch-star-field launch-stars-b" />
            <span className="launch-solar-wave wave-one" />
            <span className="launch-solar-wave wave-two" />
            <span className="launch-solar-wave wave-three" />
            <span className="launch-comet launch-comet-a" />
            <span className="launch-comet launch-comet-b" />
          </div>

          <div className="login-launch-copy">
            <span className="login-launch-kicker">AI-powered signage workspace</span>
            <h1>Bring your signage project into orbit.</h1>
            <p>Plan, configure and review every detail in one guided experience.</p>
            <button type="button" className="login-launch-button" onClick={openLoginModal}>
              <span className="login-launch-button-glow" aria-hidden="true" />
              <span className="login-launch-button-core">AI</span>
              <span>Access AI 4 Signs</span>
              <ArrowRight size={18} />
            </button>
            <span className="login-launch-hint">Secure email access · No password to remember</span>
          </div>
        </section>

        {loginModalOpen && (
          <div className="login-modal-overlay" onClick={closeLoginModal} role="presentation">
            <section
              className="login-card login-card-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="AI 4 Signs secure access"
            >
              <button type="button" className="login-modal-close" onClick={closeLoginModal} aria-label="Close login">
                <X size={19} />
              </button>

              <div className="login-cosmos login-cosmos-modal" aria-hidden="true">
                <div className="login-cosmos-nebula nebula-left" />
                <div className="login-cosmos-nebula nebula-right" />
                <div className="login-cosmos-stars stars-a" />
                <div className="login-cosmos-stars stars-b" />
                <span className="login-modal-wave modal-wave-one" />
                <span className="login-modal-wave modal-wave-two" />
                <span className="login-shooting-star shooting-a" />
                <span className="login-shooting-star shooting-b" />
              </div>

        {/* ------------------------------------------------ */}
        {/* BRAND */}
        {/* ------------------------------------------------ */}

        <div className="login-brand">

          <div className="login-logo">

            <h2>
              <span className="logo-black">Le3eD</span>
            </h2>

            <span>Software</span>

          </div>

          <div>

            <h1>AI 4 Signs</h1>

            <p>
              Secure access to your signage workspace.
            </p>

          </div>

        </div>

        {/* ------------------------------------------------ */}
        {/* EMAIL STEP */}
        {/* ------------------------------------------------ */}

        {step === "email" && (

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleContinue();
            }}
          >

            <div className="login-field">

              <label>

                <Mail size={15} />

                Business Email

              </label>

              <input
                ref={emailInputRef}
                autoFocus
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />

            </div>

            <div className="login-note">

              Enter your business email to receive a secure
              verification code.

            </div>

            <button
              type="submit"
              className={`login-primary ${emailValid ? "" : "disabled"}`}
              disabled={!emailValid || loading}
            >

              {loading
                ? <LoadingSpinner small />
                : "Continue"}

              {!loading && (
                <ArrowRight size={16} />
              )}

            </button>

          </form>

        )}

        {/* ------------------------------------------------ */}
        {/* CODE STEP */}
        {/* ------------------------------------------------ */}

        {step === "code" && (

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmCode();
            }}
          >

            <div className="login-code-header">

              <div className="login-code-icon">

                <Shield size={24} />

              </div>

              <h2>Verify your email</h2>

              <p>

                We sent a 6-digit code to

                <br />

                <strong>{maskedEmail}</strong>

              </p>

            </div>

            <input

              ref={codeRef}

              className="login-code-input"

              inputMode="numeric"

              maxLength={6}

              value={code}

              onChange={(e) =>
                setCode(
                  e.target.value
                    .replace(/[^\d]/g, "")
                    .slice(0, 6)
                )
              }

              placeholder="123456"

              disabled={loading}

            />

            <button
              type="submit"
              className={`login-primary ${code.length === 6 ? "" : "disabled"}`}
              disabled={code.length !== 6 || loading}
            >

              {loading
                ? <LoadingSpinner small />
                : "Confirm access"}

              {!loading && (
                <ArrowRight size={16} />
              )}

            </button>

            <button
              type="button"
              className="login-link-btn"
              onClick={() => {

                setStep("email");

                setCode("");

                setAlert(null);

              }}
            >
              Use another email
            </button>

            <button
              type="button"
              className="login-link-btn"
              onClick={handleResendCode}
              disabled={
                loading ||
                resendCountdown > 0
              }
            >

              {resendMessage || "Resend code"}

              {resendCountdown > 0 &&
                ` (${resendCountdown})`}

            </button>

          </form>

        )}

        {step === "company" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateCompanySendCode();
            }}
          >
            <div className="login-field">
              <label>
                <Shield size={15} />
                Company Name
              </label>

              <input
                type="text"
                placeholder="Your company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="login-note">
              This owner account is not connected to a company yet.
              Enter your company name to create your workspace.
            </div>

            <button
              type="submit"
              className={`login-primary ${canContinueCompany ? "" : "disabled"}`}
              disabled={!canContinueCompany}
            >
              {loading
                ? <LoadingSpinner small />
                : pendingLoginPayload?.companySetupToken
                  ? "Create workspace"
                  : "Send company code"}

              {!loading && <ArrowRight size={16} />}
            </button>
          </form>
        )}

        {step === "companyCode" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmCompanyCode();
            }}
          >
            <div className="login-code-header">
              <div className="login-code-icon">
                <Shield size={24} />
              </div>

              <h2>Verify company setup</h2>

              <p>
                We sent a 6-digit code to
                <br />
                <strong>{maskedEmail}</strong>
              </p>
            </div>

            <input
              ref={codeRef}
              className="login-code-input"
              inputMode="numeric"
              maxLength={6}
              value={companyCode}
              onChange={(e) =>
                setCompanyCode(
                  e.target.value
                    .replace(/[^\d]/g, "")
                    .slice(0, 6)
                )
              }
              placeholder="123456"
              disabled={loading}
            />

            <button
              type="submit"
              className={`login-primary ${companyCode.length === 6 ? "" : "disabled"}`}
              disabled={companyCode.length !== 6 || loading}
            >
              {loading ? <LoadingSpinner small /> : "Create workspace"}

              {!loading && <ArrowRight size={16} />}
            </button>

            <button
              type="button"
              className="login-secondary"
              onClick={() => {
                setStep("company");
                setCompanyCode("");
                setAlert(null);
              }}
            >
              Edit company name
            </button>

            <button
              type="button"
              className="login-link-btn"
              onClick={handleResendCompanyCode}
              disabled={loading || resendCountdown > 0}
            >
              {resendMessage || "Resend code"}

              {resendCountdown > 0 && ` (${resendCountdown})`}
            </button>
          </form>
        )}

            </section>
          </div>
        )}

        <section className="login-journey" aria-label="How AI 4 Signs works">
          <div className="login-journey-heading">
            <span className="login-journey-kicker">From idea to review</span>
            <h2>A simpler way to start your signage project.</h2>
            <p>Five guided steps. One clear workspace. No complicated software to learn.</p>
          </div>

          <div className="login-journey-flow">
            <div className="login-journey-step">
              <div className="login-journey-visual">
                <div className="login-mini-building">
                  <span className="login-mini-awning" />
                  <span className="login-mini-window left" />
                  <span className="login-mini-window right" />
                  <span className="login-mini-door" />
                </div>
                <div className="login-floating-camera"><Image size={24} /></div>
                <span className="login-journey-spark" />
              </div>
              <span className="login-journey-number">01</span>
              <h3>Upload photos</h3>
              <p>Start with a clear photo of the installation area.</p>
            </div>

            <div className="login-journey-connector"><span /></div>

            <div className="login-journey-step">
              <div className="login-journey-visual">
                <div className="login-area-canvas">
                  <span className="login-sign-zone zone-a" />
                  <span className="login-sign-zone zone-b" />
                  <span className="login-sign-zone zone-c" />
                  <span className="login-cursor-arrow">↖</span>
                </div>
              </div>
              <span className="login-journey-number">02</span>
              <h3>Place sign areas</h3>
              <p>Point to exactly where every sign should appear.</p>
            </div>

            <div className="login-journey-connector"><span /></div>

            <div className="login-journey-step">
              <div className="login-journey-visual">
                <div className="login-config-window">
                  <div className="login-config-top"><i /><i /><i /></div>
                  <div className="login-config-body">
                    <div className="login-config-preview"><span /><span /></div>
                    <div className="login-config-options"><i /><i /><i /></div>
                  </div>
                  <span className="login-config-submit">SUBMIT</span>
                </div>
              </div>
              <span className="login-journey-number">03</span>
              <h3>Choose sign types</h3>
              <p>Configure materials, lighting, colors and artwork.</p>
            </div>

            <div className="login-journey-connector"><span /></div>

            <div className="login-journey-step">
              <div className="login-journey-visual">
                <div className="login-review-sheet">
                  <span className="wide" /><span /><span className="short" />
                </div>
                <div className="login-review-check"><CheckCircle size={18} /></div>
              </div>
              <span className="login-journey-number">04</span>
              <h3>Review & submit</h3>
              <p>See your project clearly before sending it forward.</p>
            </div>

            <div className="login-journey-connector"><span /></div>

            <div className="login-journey-step">
              <div className="login-journey-visual">
                <div className="login-review-screen">
                  <div className="login-review-screen-bar"><i /><i /><i /></div>
                  <div className="login-review-screen-grid"><span /><span /><span /><span /></div>
                </div>
                <div className="login-review-people">
                  <span className="login-person person-a" />
                  <span className="login-person person-b" />
                </div>
              </div>
              <span className="login-journey-number">05</span>
              <h3>Project review</h3>
              <p>Refine details with your team in one organized place.</p>
            </div>
          </div>
        </section>

        <section className="login-benefits" aria-label="Why use AI 4 Signs">
          <div className="login-benefits-heading">
            <span className="login-benefits-kicker">Built to feel effortless</span>
            <h2>Powerful signage software without the software headache.</h2>
            <p>AI 4 Signs keeps the experience visual, guided and easy to understand — from the first click to final review.</p>
          </div>

          <div className="login-benefits-grid">
            <article className="login-benefit-card access-card">
              <div className="login-benefit-icon"><Zap size={23} /></div>
              <div className="login-access-demo" aria-hidden="true">
                <span className="login-demo-mail">you@company.com</span>
                <span className="login-demo-arrow"><ArrowRight size={15} /></span>
                <span className="login-demo-check"><CheckCircle size={17} /></span>
              </div>
              <h3>Easy access</h3>
              <p>Secure email access keeps sign-in simple. No complicated account setup or training required.</p>
            </article>

            <article className="login-benefit-card friendly-card">
              <div className="login-benefit-icon"><MousePointer size={23} /></div>
              <div className="login-friendly-demo" aria-hidden="true">
                <div className="login-friendly-window">
                  <span className="login-friendly-sidebar" />
                  <span className="login-friendly-block block-one" />
                  <span className="login-friendly-block block-two" />
                  <span className="login-friendly-block block-three" />
                </div>
                <span className="login-friendly-cursor">↖</span>
              </div>
              <h3>Actually user friendly</h3>
              <p>Clear actions, visual guidance and a focused workflow help anyone move through a project with confidence.</p>
            </article>

            <article className="login-benefit-card workspace-card">
              <div className="login-benefit-icon"><Layers size={23} /></div>
              <div className="login-workspace-demo" aria-hidden="true">
                <span className="workspace-card-one" />
                <span className="workspace-card-two" />
                <span className="workspace-card-three" />
                <span className="workspace-pulse" />
              </div>
              <h3>Everything stays together</h3>
              <p>Photos, sign locations, specifications and review details stay connected inside one workspace.</p>
            </article>
          </div>

          <div className="login-product-story">
            <div className="login-product-orbit" aria-hidden="true">
              <span className="orbit-dot dot-one" />
              <span className="orbit-dot dot-two" />
              <span className="orbit-dot dot-three" />
              <div className="login-product-core">AI</div>
            </div>

            <div className="login-product-copy">
              <span className="login-product-label">A Le3eD Software product</span>
              <h2>AI 4 Signs is designed and built by <a className="login-le3ed-link" href="https://le3ed.com" target="_blank" rel="noreferrer">Le3eD Software.</a></h2>
              <p>We build practical software experiences that turn complex business workflows into tools people can understand and enjoy using.</p>
              <div className="login-product-points">
                <span><Monitor size={15} /> Purpose-built workflow</span>
                <span><Users size={15} /> Made for real teams</span>
                <span><Lock size={15} /> Secure by design</span>
              </div>
            </div>

            <a className="login-le3ed-mark" href="https://le3ed.com" target="_blank" rel="noreferrer" aria-label="Visit Le3eD Software website">
              <span className="login-le3ed-code">&lt;/&gt;</span>
              <div><strong>Le3eD</strong><span>Software</span></div>
              <ArrowRight className="login-le3ed-arrow" size={17} />
            </a>
          </div>
        </section>

      </div>

      {accessRequestOpen && (
        <div
          className="access-request-overlay"
          onClick={closeAccessRequest}
          role="presentation"
        >
          <div
            className="access-request-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="access-request-title"
          >
            <button
              type="button"
              className="access-request-close"
              onClick={closeAccessRequest}
              aria-label="Close"
              disabled={accessRequestLoading}
            >
              <X size={18} />
            </button>

            {accessRequestState === "form" ? (
              <>
                <div className="access-request-visual" aria-hidden="true">
                  <div className="access-request-orbit orbit-one" />
                  <div className="access-request-orbit orbit-two" />
                  <div className="access-request-mail">
                    <Mail size={30} />
                  </div>
                  <span className="access-request-spark spark-one" />
                  <span className="access-request-spark spark-two" />
                  <span className="access-request-spark spark-three" />
                </div>

                <div className="access-request-copy">
                  <span className="access-request-kicker">Access request</span>
                  <h2 id="access-request-title">Let's get you approved</h2>
                  <p>
                    This email is not registered with an AI 4 Signs company yet.
                    Send a request and our team will review it.
                  </p>
                </div>

                <div className="access-request-fields">
                  <label className="access-request-field">
                    <span><User size={15} /> Your name</span>
                    <input
                      type="text"
                      value={accessRequestName}
                      onChange={(e) => setAccessRequestName(e.target.value)}
                      placeholder="Your full name"
                      autoFocus
                      disabled={accessRequestLoading}
                    />
                  </label>

                  <label className="access-request-field is-readonly">
                    <span><Mail size={15} /> Business email</span>
                    <input
                      type="email"
                      value={email.trim().toLowerCase()}
                      readOnly
                      tabIndex={-1}
                    />
                  </label>
                </div>

                <div className="access-request-notice">
                  <Clock size={17} />
                  <span>You'll receive an email as soon as your access is approved.</span>
                </div>

                {accessRequestMessage && (
                  <div className="access-request-error">
                    {accessRequestMessage}
                  </div>
                )}

                <button
                  type="button"
                  className={`access-request-submit ${accessRequestName.trim().length >= 2 ? "" : "disabled"}`}
                  onClick={handleRequestAccess}
                  disabled={accessRequestName.trim().length < 2 || accessRequestLoading}
                >
                  {accessRequestLoading ? (
                    <LoadingSpinner small />
                  ) : (
                    <>
                      Request access
                      <ArrowRight size={17} />
                    </>
                  )}
                </button>
              </>
            ) : (
              <div className="access-request-result">
                <div className={`access-request-success-icon ${accessRequestState === "pending" ? "is-pending" : ""}`}>
                  {accessRequestState === "pending"
                    ? <Clock size={42} />
                    : <CheckCircle size={42} />}
                </div>

                <span className="access-request-kicker">
                  {accessRequestState === "authorized"
                    ? "Access available"
                    : accessRequestState === "pending"
                      ? "Awaiting approval"
                      : "Request received"}
                </span>

                <h2 id="access-request-title">
                  {accessRequestState === "authorized"
                    ? "Your email is already approved"
                    : accessRequestState === "pending"
                      ? "Your request is already under review"
                      : "You're on the list"}
                </h2>

                <p>
                  {accessRequestState === "authorized"
                    ? "Close this window and continue with your email to access AI 4 Signs."
                    : accessRequestState === "pending"
                      ? "Your email is awaiting approval from the AI 4 Signs team. We'll email you as soon as your access is approved."
                      : "We'll email you as soon as your access is approved. No need to submit another request."}
                </p>

                <div className="access-request-email-pill">
                  <Mail size={15} />
                  {email.trim().toLowerCase()}
                </div>

                <button
                  type="button"
                  className="access-request-submit"
                  onClick={closeAccessRequest}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {accessDeniedOpen && (
        <div
          className="modal-overlay"
          onClick={() => setAccessDeniedOpen(false)}
        >
          <div
            className="modal-card apple"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Access Denied</h3>

            <p>
              This email is not authorized to access <strong>AI 4 Signs</strong>.
            </p>

            <p style={{ color: "#666", fontSize: "14px" }}>
              If you believe this is an error, please contact your company administrator.
            </p>

            <button
              className="primary"
              onClick={() => setAccessDeniedOpen(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

    </main>

  );

}